import React, { useCallback, useRef, useEffect } from "react";
import stars from "../../../../../assets/stars.png";
import { FaCircleChevronRight, FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { progressService } from "../../../../../services/progressService.js";

const LastPage = ({ 
  selectedCards, 
  unmetNeedsData, 
  onBack, 
  userId, 
  subsectionId, 
  course, 
  onSelectModule, 
  onSelectSubsection 
}) => {
  const subsectionIdRef = useRef(subsectionId);

  // ✅ Keep ref synced with prop
  useEffect(() => {
    if (subsectionId) subsectionIdRef.current = subsectionId;
  }, [subsectionId]);

  // ✅ Function to handle moving to next section
  const handleNextSection = useCallback(async () => {
    console.log("🟡 handleNextSection triggered");
    console.log("Props check →", { course, userId, subsectionId: subsectionIdRef.current });

    if (!course || !onSelectModule || !onSelectSubsection) {
      console.warn("⚠️ Missing navigation props (course, onSelectModule, onSelectSubsection)");
      alert("Navigation props missing. Please ensure you're in a valid course context.");
      return;
    }

    try {
      // ✅ Mark current subsection completed
      console.log("💾 Marking current subsection completed...");
      await progressService.saveProgress(
        userId,
        subsectionIdRef.current,
        { status: "completed" },
        "completed"
      );
      console.log("✅ Marked subsection as completed!");
    } catch (err) {
      console.error("❌ Failed to mark subsection completed:", err);
    }

    // ✅ Find next subsection
    const allSubs =
      course?.modules
        ?.sort((a, b) => a.order - b.order)
        ?.flatMap((m) =>
          m.subsections
            ?.sort((a, b) => a.order - b.order)
            ?.map((s) => ({ ...s, moduleId: m.id, module: m }))
        ) || [];

    if (!allSubs.length) {
      console.warn("⚠️ No subsections found in course");
      alert("No subsections found in course!");
      return;
    }

    const currentIndex = allSubs.findIndex(
      (s) => s.id === subsectionIdRef.current
    );

    if (currentIndex === -1) {
      console.warn("⚠️ Current subsection not found");
      alert("Current subsection not found!");
      return;
    }

    if (currentIndex >= allSubs.length - 1) {
      console.log("🎉 Already at the last subsection!");
      alert("You’ve reached the last section of this course!");
      return;
    }

    const nextSub = allSubs[currentIndex + 1];
    console.log("➡️ Navigating to next subsection:", nextSub.title);

    // ✅ Navigate to next module/subsection
    onSelectModule(nextSub.module);
    onSelectSubsection(nextSub);

    // alert(`✅ Moved to next section: ${nextSub.title}`);
  }, [course, onSelectModule, onSelectSubsection, userId]);

  // ✅ Selected unmet needs data
  const selectedUnmetNeeds = unmetNeedsData.filter(item =>
    selectedCards.includes(item.id)
  );
  return (
    <div className="min-h-screen w-full bg-white flex flex-col items-center px-4 py-10 relative">
      {/* Timeline Line */}
      <div className="absolute top-1/2 left-0 w-full h-[5px] bg-[#E6E6E6] -translate-y-1/2 hidden lg:block" />

      {/* Three timeline circles */}
      <div className="absolute top-1/2 w-full flex justify-between px-10 pr-20 -translate-y-1/2 hidden lg:flex">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="w-16 h-16 bg-white border-[4.77px] border-[#E6E6E6] rounded-full flex flex-col items-center justify-center gap-2"
          >
            <div className="w-8 h-8 bg-[#E6E6E6] border-[6px] border-[#E6E6E6] rounded-full"></div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-3 gap-12 items-center lg:mt-10 mt-20">
        {/* LEFT — UNMET NEEDS */}
        <div className="text-center z-10 max-w-3xl">
          <div className="flex flex-col justify-center items-center px-4 py-10 relative overflow-hidden">
            {/* Main Card */}
            <div className="rounded-[22.2px] bg-white max-w-md w-full border border-[#707070] flex flex-col items-center min-h-[500px] relative overflow-hidden">
              {/* Header */}
              <div className="bg-[#0D1111] text-white w-full py-6 rounded-t-[22.2px] h-[115px] flex items-center justify-center gap-3">
                <img src={stars} alt="Stars" className="w-6 h-6 rotate-180 opacity-90" />
                <h1 className="text-2xl text-white font-bold font-gotham tracking-wide">
                  UNMET NEEDS
                </h1>
                <img src={stars} alt="Stars" className="w-6 h-6 opacity-90" />
              </div>

              {/* Selected Items */}
              {selectedUnmetNeeds.length > 0 && (
                <>
                  {/* Top Item */}
                  <div className="w-full px-6 -mt-5 z-10">
                    <div className="bg-[#F8F8F8] border border-[#CCCCCC] rounded-2xl py-3 px-5 text-center font-gotham font-medium text-[#1D1D1D] shadow-sm">
                      {selectedUnmetNeeds[0].label}
                    </div>
                  </div>

                  {/* Remaining List */}
                  <div className="w-full px-6 pb-8 mt-4">
                    {selectedUnmetNeeds.slice(1).map((need) => (
                      <div
                        key={need.id}
                        className="bg-white border border-[#E0E0E0] rounded-2xl py-3 px-5 text-center font-gotham font-medium text-[#1D1D1D] shadow-sm mb-3 hover:bg-[#F8F8F8] transition"
                      >
                        {need.label}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* ✅ Next Section Button */}
            <div className="mt-8 w-full max-w-md">
              <button
                onClick={handleNextSection}
                className="w-full bg-[#54B0AF] text-white py-3 px-6 rounded-full font-gotham font-medium text-base hover:bg-[#469F9E] hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Next: Value Mapping
                <FaCircleChevronRight className="ml-16" />
              </button>
            </div>
          </div>
        </div>

        {/* CENTER — PRESENT */}
        <div className="relative flex justify-center">
          <h3 className="font-medium text-[#000000] font-gotham text-[14px] mt-28 uppercase tracking-wide">
            Present Value Mapping
          </h3>
        </div>

        {/* RIGHT — FUTURE */}
        <div className="relative flex justify-center">
          <h3 className="font-medium text-[#000000] font-gotham text-[14px] mt-28 uppercase tracking-wide">
            Future Value Mapping
          </h3>
        </div>
      </div>

      {/* 🔼🔽 Navigation Arrows */}
      <div className="flex items-center justify-end gap-2 absolute right-0 bottom-0">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-2 py-2 font-gotham bg-[#B6B6B6] rounded-full hover:bg-[#54B0AF] font-medium transition-all"
        >
          <FaChevronUp className="w-4 h-4 font-gotham -rotate-90 text-white" />
        </button>

        <button
          onClick={handleNextSection}
          className="inline-flex items-center gap-2 px-2 py-2 font-gotham bg-[#54B0AF] rounded-full hover:bg-[#469F9E] font-medium transition-all"
        >
          <FaChevronDown className="w-4 h-4 font-gotham -rotate-90 text-white" />
        </button>
      </div>
    </div>
  );
};

export default LastPage;
